import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Abas } from '../../componentes/abas';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { BuscaRemota, CampoControlado, CampoData, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { ariaErro, useValidacaoLocal } from '../../componentes/validacao-local';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post, getTodos } from '../../nucleo/api';
import { data, dataHora, dinheiro, nomeCadastro, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { invalidarEstoque, qtd, rotuloProduto, useDepositos } from './comum';
import type { Movimentacao, SaldoDetalhado, TipoMov } from './tipos';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { TextoCortado } from '../../componentes/texto-cortado';

const COR: Record<TipoMov, CorSelo> = { entrada: 'verde', saida: 'laranja', transferencia: 'azul', ajuste: 'roxo' };
const TIPOS: TipoMov[] = ['entrada', 'saida', 'transferencia', 'ajuste'];

/** M9.3: lista de movimentações com filtros e drawer para registrar entrada, saída, transferência ou ajuste. */
export function Movimentacoes() {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [novo, setNovo] = useState(false);
  const [tipo, setTipo] = useState('');
  const [deposito, setDeposito] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const depositos = useDepositos();

  const colunas: Coluna<Movimentacao>[] = [
    { chave: 'data', rotulo: t('campos.data'), mono: true, render: (m) => dataHora(m.data) },
    { chave: 'tipo', rotulo: t('campos.tipo'), render: (m) => <Selo cor={COR[m.tipo]}>{t(`tipos.${m.tipo}`)}</Selo> },
    {
      chave: 'produto_nome',
      rotulo: t('campos.produto'),
      principal: true,
      render: (m) => (
        <span className="flex max-w-64 min-w-0 flex-col">
          <span className="truncate font-medium" title={m.produto_nome ?? undefined}>{nomeCadastro(m.produto_nome)}</span>
          <TextoCortado className="numero text-xs text-tinta-3">{[m.produto_codigo, m.variacao_sku].filter(Boolean).join(' / ')}</TextoCortado>
        </span>
      ),
    },
    { chave: 'deposito_nome', rotulo: t('campos.deposito'), render: (m) => (m.deposito_destino_nome ? `${m.deposito_nome} > ${m.deposito_destino_nome}` : m.deposito_nome) },
    { chave: 'lote_codigo', rotulo: t('campos.lote'), mono: true, oculta: true, render: (m) => m.lote_codigo ?? '' },
    {
      chave: 'quantidade',
      rotulo: t('campos.quantidade'),
      alinhar: 'direita',
      mono: true,
      render: (m) => {
        const sinal = m.tipo === 'transferencia' ? '' : m.efeito_total > 0 ? '+' : '-';
        return <span className={m.efeito_total > 0 ? 'text-verde' : m.efeito_total < 0 ? 'text-laranja' : ''}>{`${sinal}${qtd(m.quantidade)} ${m.unidade ?? ''}`}</span>;
      },
    },
    { chave: 'custo_unitario_centavos', rotulo: t('campos.custoUnitario'), alinhar: 'direita', mono: true, oculta: true, render: (m) => dinheiro(m.custo_unitario_centavos) },
    { chave: 'valor_centavos', rotulo: t('campos.valor'), alinhar: 'direita', mono: true, render: (m) => dinheiro(m.valor_centavos) },
    { chave: 'motivo', rotulo: t('campos.motivo'), render: (m) => <TextoCortado className="block max-w-48">{descricaoGerada(m.motivo)}</TextoCortado> },
    { chave: 'autor_nome', rotulo: t('campos.autor'), oculta: true, render: (m) => m.autor_nome ?? '' },
  ];

  return (
    <>
      {(s.pode('estoque.movimentar') || s.pode('estoque.ajustar')) && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('acoes.novaMovimentacao')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Movimentacao>
        tela="estoque.movimentacoes"
        caminho="/estoque/movimentacoes"
        params={{ tipo: tipo || undefined, deposito_id: deposito || undefined, de: de || undefined, ate: ate || undefined }}
        filtros={{ tipo, deposito_id: deposito, de, ate }}
        aoAplicarFiltros={(f) => {
          setTipo(String(f.tipo));
          setDeposito(String(f.deposito_id));
          setDe(String(f.de));
          setAte(String(f.ate));
        }}
        colunas={colunas}
        vazio={<Vazio texto={t('vazio.movimentacoes')} />}
        filtrosExtra={
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-full sm:w-56">
              <Selecao aria-label={t('campos.tipo')} vazio={tn('comum.todos')} opcoes={TIPOS.map((x) => ({ valor: x, rotulo: t(`tipos.${x}`) }))} value={tipo} onChange={(e) => setTipo(e.target.value)} />
            </div>
            <div className="w-full sm:w-56">
              <Selecao aria-label={t('campos.deposito')} vazio={t('campos.todosDepositos')} opcoes={(depositos.data?.itens ?? []).map((d) => ({ valor: d.id, rotulo: d.nome }))} value={deposito} onChange={(e) => setDeposito(e.target.value)} />
            </div>
            <div className="w-full sm:w-40">
              <CampoData type="date" rotulo={t('campos.de')} value={de} onChange={(e) => setDe(e.target.value)} />
            </div>
            <div className="w-full sm:w-40">
              <CampoData type="date" rotulo={t('campos.ate')} value={ate} onChange={(e) => setAte(e.target.value)} />
            </div>
          </div>
        }
      />
      <FormMovimentacao aberto={novo} aoFechar={() => setNovo(false)} />
    </>
  );
}

export interface ProdutoParaMovimento {
  id: string;
  codigo: string;
  nome: string;
  unidade: string;
  custo_centavos: number;
  controla_lote: boolean;
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  produto?: ProdutoParaMovimento;
  tipoInicial?: TipoMov;
  quantidadeInicial?: number;
}

/** Drawer de nova movimentação. O conteúdo só monta com o drawer aberto, então o estado zera a cada uso. */
export function FormMovimentacao({ aberto, aoFechar, produto, tipoInicial, quantidadeInicial }: Props) {
  const { t } = useTranslation('estoque');
  return (
    <Drawer aberto={aberto} aoFechar={aoFechar} titulo={t('acoes.novaMovimentacao')}>
      <Corpo aoFechar={aoFechar} produto={produto} tipoInicial={tipoInicial} quantidadeInicial={quantidadeInicial} />
    </Drawer>
  );
}

function Corpo({ aoFechar, produto, tipoInicial, quantidadeInicial }: Omit<Props, 'aberto'>) {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const depositos = useDepositos();
  const tiposPermitidos = TIPOS.filter((x) => (x === 'ajuste' ? s.pode('estoque.ajustar') : s.pode('estoque.movimentar')));
  const [tipo, setTipo] = useState<TipoMov>(tipoInicial && tiposPermitidos.includes(tipoInicial) ? tipoInicial : (tiposPermitidos[0] ?? 'entrada'));
  const [prod, setProd] = useState<ProdutoParaMovimento | null>(produto ?? null);
  const [variacao, setVariacao] = useState<string | null>(null);
  const [deposito, setDeposito] = useState<string>('');
  const [destino, setDestino] = useState<string>('');
  const [local, setLocal] = useState<string | null>(null);
  const [lote, setLote] = useState<string | null>(null);
  const [loteCodigo, setLoteCodigo] = useState('');
  const [validade, setValidade] = useState('');
  const [quantidade, setQuantidade] = useState<number | null>(quantidadeInicial ?? null);
  const [custo, setCusto] = useState<number | null>(produto ? produto.custo_centavos : null);
  const [sentido, setSentido] = useState<string>('saida');
  const [motivo, setMotivo] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const listaDepositos = depositos.data?.itens ?? [];
  const depositoEfetivo = deposito || listaDepositos.find((d) => d.padrao)?.id || '';
  const depositoDoLocal = tipo === 'transferencia' ? destino : depositoEfetivo;

  const saldo = useQuery({ queryKey: ['/estoque/produtos', 'saldo', prod?.id], queryFn: () => get<SaldoDetalhado>(`/estoque/produtos/${prod!.id}/saldo`), enabled: !!prod });
  const temVariacoes = useQuery({
    queryKey: ['/estoque/variacoes', 'tem', prod?.id],
    queryFn: () => get<{ itens: unknown[] }>('/estoque/variacoes', { produto_id: prod!.id, ativo: true, limite: 1 }),
    enabled: !!prod,
  });
  const locais = useQuery({
    queryKey: ['/estoque/locais', depositoDoLocal],
    queryFn: () => getTodos<{ id: string; codigo: string; descricao: string | null }>('/estoque/locais', { deposito_id: depositoDoLocal }),
    enabled: !!depositoDoLocal,
  });

  const enviar = useMutation({
    mutationFn: () => {
      const comum = { produto_id: prod!.id, variacao_id: variacao, quantidade, motivo: motivo.trim() || null };
      if (tipo === 'entrada') {
        return post(`/estoque/movimentacoes/entrada`, {
          ...comum,
          deposito_id: depositoEfetivo || null,
          local_id: local,
          custo_unitario_centavos: custo,
          lote: loteCodigo.trim() ? { codigo: loteCodigo.trim(), validade: validade || null } : null,
        });
      }
      if (tipo === 'transferencia') return post('/estoque/movimentacoes/transferencia', { ...comum, deposito_id: depositoEfetivo, deposito_destino_id: destino, local_id: local, lote_id: lote });
      if (tipo === 'ajuste') return post('/estoque/movimentacoes/ajuste', { ...comum, deposito_id: depositoEfetivo || null, lote_id: lote, sentido });
      return post('/estoque/movimentacoes/saida', { ...comum, deposito_id: depositoEfetivo || null, local_id: local, lote_id: lote });
    },
    onSuccess: async () => {
      await invalidarEstoque(qc);
      toast.sucesso(tn('comum.salvo'));
      aoFechar();
    },
    onError: (e) => {
      setErroGeral(mensagemDeErro(e));
      toast.erro(mensagemDeErro(e));
    },
  });

  const { ref: refForm } = useValidacaoLocal(erros, erroGeral);

  function validar() {
    const e: Record<string, string> = {};
    if (!prod) e.produto = t('erros.produto');
    if (!quantidade || quantidade <= 0) e.quantidade = t('erros.quantidade');
    if (tipo === 'transferencia' && (!destino || destino === depositoEfetivo)) e.destino = t('erros.destino');
    if (tipo === 'ajuste' && !motivo.trim()) e.motivo = t('erros.motivoAjuste');
    if (prod?.controla_lote && !(tipo === 'entrada' ? loteCodigo.trim() : lote)) e.lote = t('erros.lote');
    setErros(e);
    return Object.keys(e).length === 0;
  }

  const saldoNoDeposito = saldo.data?.depositos.find((d) => d.deposito_id === depositoEfetivo)?.saldo ?? 0;
  const opcoesDeposito = listaDepositos.map((d) => ({ valor: d.id, rotulo: d.nome }));

  return (
    <form
      ref={refForm}
      noValidate
      className="grid grid-cols-1 gap-4 sm:grid-cols-6"
      onSubmit={(ev) => {
        ev.preventDefault();
        setErroGeral(null);
        if (validar()) enviar.mutate();
      }}
    >
      {erroGeral && (
        <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-6">
          {erroGeral}
        </p>
      )}

      <Abas variante="chips" className="sm:col-span-6" rotulo={t('campos.tipo')} ativa={tipo} aoMudar={setTipo} itens={tiposPermitidos.map((x) => ({ chave: x, rotulo: t(`tipos.${x}`) }))} />

      <div className="sm:col-span-6">
        <Rotulo id="mov-produto" rotulo={t('campos.produto')} obrigatorio erro={erros.produto}>
          <BuscaRemota
            id="mov-produto"
            {...ariaErro('mov-produto', erros.produto)}
            def={{ caminho: '/estoque/produtos', params: { ativo: true }, rotulo: rotuloProduto }}
            valor={prod?.id ?? null}
            rotuloAtual={prod ? `${prod.codigo} ${prod.nome}` : undefined}
            aoMudar={(v, item) => {
              setVariacao(null);
              setLote(null);
              setProd(v && item ? { id: String(item.id), codigo: String(item.codigo), nome: String(item.nome), unidade: String(item.unidade), custo_centavos: Number(item.custo_centavos), controla_lote: !!item.controla_lote } : null);
              if (item && tipo === 'entrada') setCusto(Number(item.custo_centavos));
            }}
          />
        </Rotulo>
        {prod && saldo.data && (
          <p className="mt-1.5 text-xs text-tinta-3">
            {t('movimentacao.saldoAtual', { total: `${qtd(saldo.data.saldo_total)} ${prod.unidade ?? ''}`, deposito: `${qtd(saldoNoDeposito)} ${prod.unidade ?? ''}` })}
          </p>
        )}
      </div>

      {prod && (temVariacoes.data?.itens.length ?? 0) > 0 && (
        <div className="sm:col-span-6">
          <Rotulo id="mov-variacao" rotulo={t('campos.variacao')}>
            <BuscaRemota
              key={prod.id}
              id="mov-variacao"
              def={{ caminho: '/estoque/variacoes', params: { produto_id: prod.id, ativo: true }, rotulo: (i) => `${String(i.sku)} ${String(i.rotulo)}` }}
              valor={variacao}
              aoMudar={(v) => setVariacao(v)}
            />
          </Rotulo>
        </div>
      )}

      <div className={tipo === 'transferencia' ? 'sm:col-span-3' : 'sm:col-span-6'}>
        <Rotulo id="mov-deposito" rotulo={tipo === 'transferencia' ? t('campos.depositoOrigem') : t('campos.deposito')}>
          <Selecao id="mov-deposito" opcoes={opcoesDeposito} value={depositoEfetivo} onChange={(e) => setDeposito(e.target.value)} />
        </Rotulo>
      </div>
      {tipo === 'transferencia' && (
        <div className="sm:col-span-3">
          <Rotulo id="mov-destino" rotulo={t('campos.depositoDestino')} obrigatorio erro={erros.destino}>
            <Selecao id="mov-destino" {...ariaErro('mov-destino', erros.destino)} opcoes={opcoesDeposito.filter((d) => d.valor !== depositoEfetivo)} vazio={tn('comum.selecione')} value={destino} onChange={(e) => setDestino(e.target.value)} />
          </Rotulo>
        </div>
      )}

      {tipo !== 'ajuste' && (
        <div className="sm:col-span-3">
          <Rotulo id="mov-local" rotulo={tipo === 'transferencia' ? t('campos.localDestino') : t('campos.local')}>
            <Selecao
              id="mov-local"
              opcoes={(locais.data?.itens ?? []).map((l) => ({ valor: l.id, rotulo: l.descricao ? `${l.codigo} (${textoLivre(l.descricao)})` : l.codigo }))}
              vazio={tn('comum.nenhum')}
              value={local ?? ''}
              onChange={(e) => setLocal(e.target.value || null)}
            />
          </Rotulo>
        </div>
      )}

      {tipo === 'entrada' ? (
        <>
          <div className="sm:col-span-3">
            <Rotulo id="mov-lote-codigo" rotulo={t('campos.codigoLote')} obrigatorio={!!prod?.controla_lote} erro={erros.lote} ajuda={prod?.controla_lote ? t('ajuda.loteObrigatorio') : t('ajuda.loteOpcional')}>
              <Entrada id="mov-lote-codigo" {...ariaErro('mov-lote-codigo', erros.lote)} value={loteCodigo} onChange={(e) => setLoteCodigo(e.target.value)} maxLength={60} />
            </Rotulo>
          </div>
          <div className="sm:col-span-3">
            <Rotulo id="mov-validade" rotulo={t('campos.validade')}>
              <CampoData id="mov-validade" type="date" value={validade} onChange={(e) => setValidade(e.target.value)} disabled={!loteCodigo.trim()} />
            </Rotulo>
          </div>
        </>
      ) : (
        prod && (
          <div className="sm:col-span-3">
            <Rotulo id="mov-lote" rotulo={t('campos.lote')} obrigatorio={!!prod.controla_lote} erro={erros.lote}>
              <BuscaRemota
                key={prod.id}
                id="mov-lote"
                {...ariaErro('mov-lote', erros.lote)}
                def={{ caminho: '/estoque/lotes', params: { produto_id: prod.id, com_saldo: tipo === 'ajuste' && sentido === 'entrada' ? undefined : true }, rotulo: (i) => `${String(i.codigo)}${i.validade ? ` (${data(String(i.validade))})` : ''}` }}
                valor={lote}
                aoMudar={(v) => setLote(v)}
              />
            </Rotulo>
          </div>
        )
      )}

      {tipo === 'ajuste' && (
        <div className="sm:col-span-3">
          <CampoControlado def={{ nome: 'sentido', rotulo: t('campos.sentido'), tipo: 'selecao', obrigatorio: true, opcoes: [{ valor: 'saida', rotulo: t('sentido.saida') }, { valor: 'entrada', rotulo: t('sentido.entrada') }] }} valor={sentido} aoMudar={(v) => setSentido(String(v ?? 'saida'))} />
        </div>
      )}

      <div className="sm:col-span-3">
        <CampoControlado
          def={{ nome: 'quantidade', rotulo: prod ? `${t('campos.quantidade')} (${prod.unidade ?? ''})` : t('campos.quantidade'), tipo: 'numero', obrigatorio: true, min: 0, passo: 0.0001 }}
          valor={quantidade}
          erro={erros.quantidade}
          aoMudar={(v) => setQuantidade(v as number | null)}
        />
      </div>
      {tipo === 'entrada' && (
        <div className="sm:col-span-3">
          <CampoControlado def={{ nome: 'custo', rotulo: t('campos.custoUnitario'), tipo: 'dinheiro', ajuda: t('ajuda.custoEntrada') }} valor={custo} aoMudar={(v) => setCusto(v as number | null)} />
        </div>
      )}

      <div className="sm:col-span-6">
        <CampoControlado def={{ nome: 'motivo', rotulo: t('campos.motivo'), tipo: 'area', obrigatorio: tipo === 'ajuste' }} valor={motivo} erro={erros.motivo} aoMudar={(v) => setMotivo(String(v ?? ''))} />
      </div>

      <div className="flex justify-end gap-2 sm:col-span-6">
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.cancelar')}
        </Botao>
        <Botao variante="primario" type="submit" carregando={enviar.isPending}>
          {t('acoes.registrar')}
        </Botao>
      </div>
    </form>
  );
}
